import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { m as motion, AnimatePresence } from "framer-motion";
import { NEXT_COHORT_NAV_LABEL, NEXT_COHORT_PATH } from "@/lib/cohorts";
import { MEMBER_SHOWCASE_PATH } from "@/lib/routes";

const navItems = [
  { label: "Curriculum", to: "/curriculum" },
  { label: "Membership", to: "/membership" },
  { label: "Workshops", to: "/workshops" },
  { label: "FAQ", to: "/faq" },
  { label: "Member Showcase", to: MEMBER_SHOWCASE_PATH },
  { label: "About Us", to: "/about-us" },
];

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => setIsOpen(!isOpen);

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 z-50 w-full transition-[background,backdrop-filter,border-color,box-shadow] duration-300 ${
        scrolled
          ? "glass-strong border-b border-border/50 shadow-depth-md"
          : "glass border-b border-border/20"
      }`}
    >
      <nav className="section-inner py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="group">
            <motion.img
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 400 }}
              src="/images/ai-for-hr-mastermind-logo.png"
              alt="AI for HR Mastermind"
              className="h-10 sm:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1 lg:gap-2">
            {navItems.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="px-3 py-1.5 text-sm text-foreground/80 hover:text-primary font-medium transition-colors duration-300 rounded-lg hover:bg-primary/5"
              >
                {item.label}
              </Link>
            ))}

            <div className="ml-2 flex items-center gap-2">
              <Link
                to={NEXT_COHORT_PATH}
                className="hidden xl:inline text-xs font-semibold text-primary bg-primary/10 px-3 py-1.5 rounded-full whitespace-nowrap hover:bg-primary/20 transition-colors"
              >
                {NEXT_COHORT_NAV_LABEL}
              </Link>
              <a
                href="https://members.aiforhrmastermind.com/login"
                className="px-4 py-2 rounded-xl bg-secondary text-secondary-foreground text-sm font-semibold shadow-secondary hover:shadow-glow-secondary hover:-translate-y-0.5 transition-all duration-300"
              >
                Member Login
              </a>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="md:hidden p-2 rounded-lg glass hover:glass-strong transition-all duration-300"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden overflow-hidden"
            >
              <div className="py-4 space-y-2">
                <Link
                  to={NEXT_COHORT_PATH}
                  onClick={() => setIsOpen(false)}
                  className="block px-4 py-2 text-xs font-semibold text-primary uppercase tracking-wider"
                >
                  {NEXT_COHORT_NAV_LABEL}
                </Link>

                {navItems.map((item) => (
                  <Link
                    key={item.label}
                    to={item.to}
                    onClick={() => setIsOpen(false)}
                    className="block px-4 py-3 rounded-lg glass-strong hover:glass text-foreground font-medium transition-all duration-300"
                  >
                    {item.label}
                  </Link>
                ))}

                <a
                  href="https://members.aiforhrmastermind.com/login"
                  className="block px-4 py-3 rounded-lg bg-secondary text-secondary-foreground text-center font-semibold shadow-secondary"
                >
                  Member Login
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </motion.header>
  );
};
